import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { 
    Typography, 
    Card, 
    Select,
} from "@material-tailwind/react";

import { 
    NavArrowDown, 
    NavArrowUp,
} from "iconoir-react";

import {
    listStatsRequest,
} from '../../../redux/stats/actions';


interface KpiCardPropsType {
    title: string;
    value: string;
    percentage: string;
    isIncreasing: boolean;
}

export function KpiCard({
    title,
    value,
    percentage,
    isIncreasing,
}: KpiCardPropsType) {
    return (
        <Card>
            <Card.Header className="w-full flex items-center gap-4 justify-between m-0 p-4">
                <Typography
                    as="p"
                    type="small"
                    className="text-xs text-foreground/70 font-semibold block"
                >
                    {title}
                </Typography>
                <Typography
                    as="p"
                    type="small"
                    className="text-xs font-semibold block"
                    color={isIncreasing ? "success" : "error"}
                >
                    {isIncreasing ? (
                        <NavArrowUp className="w-4 h-4 stroke-2 inline-block mr-1" />
                        ) : (
                            <NavArrowDown className="w-4 h-4 stroke-2 inline-block mr-1" />
                        )
                    }
                    {percentage}%
                </Typography>
            </Card.Header>
            <Card.Body className="px-4 pt-0 pb-4">
                <Typography as="p" type="h5">
                    {value}
                </Typography>
            </Card.Body>
        </Card>
    );
}

interface KpiSectionPropsType {
    className: string;
}

const get_perc = (n: number, o: number) => {
    if (!o)
        return 100;
    return Math.round(n*100/o);
};

const FIELD_MAP = [
    {'field': "sales_amount", "title": "Cars Sold Last Month"},
    {'field': "inventory_count", "title": "Cars on Inventory"},
    {'field': "capital_in_cars", "title": "Capital on Cars"},
    {'field': "capital_in_parts", "title": "Capital on Parts"},
]

type KpiData = {
    title: string;
    value: string;
    percentage: number;
    isIncreasing: boolean;
};

export function KpiSection({
    className,
}: KpiSectionPropsType) {
    const dispatch = useDispatch();
    const { stats } = useSelector((state: any) => state.stats);
    const [period, setPeriod] = useState('day');
    const [data, setData] = useState<KpiData[]>([]);

    useEffect(() => {
        dispatch(listStatsRequest({period: period}));
    }, [dispatch, period]);

    useEffect(() => {
        if (!stats)
            return;

        setData(FIELD_MAP.map((el) => (
            {
                title: el.title,
                value: stats[el.field].current.toLocaleString('en-US'),
                percentage: get_perc(stats[el.field].current, stats[el.field].previous),
                isIncreasing: stats[el.field].current > stats[el.field].previous
            }))
        );
    }, [stats]);

    return (
        <section className={className}>
            <div className="flex sm:items-center flex-wrap sm:flex-row flex-col gap-6 sm:justify-between">
                <div>
                    <Typography className="font-semibold mb-0.5">
                        Overall Performance
                    </Typography>
                    <Typography as="p" type="small" className="text-foreground">
                        Upward arrow indicating an increase in revenue compared to the
                        previous period.
                    </Typography>
                </div>
                <Select value={period} onValueChange={(val) => setPeriod(val)}>
                    <Select.Trigger
                        className="w-full sm:w-max"
                        placeholder="Select Date"
                    />
                    <Select.List>
                        <Select.Option value="prev_year">Previous Year</Select.Option>
                        <Select.Option value="year">Last Year</Select.Option>
                        <Select.Option value="month">Last Month</Select.Option>
                        <Select.Option value="day">Last 24H</Select.Option>
                    </Select.List>
                </Select>
            </div>
            <div className="grid mt-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {data.map((props, key) => (
                    <KpiCard key={key} {...(props as any)} />
                ))}
            </div>
        </section>
    );
}
